// ─── Campaign Block Animations ────────────────────────────────────────────
// CSS-only animation system — no external libraries

export type AnimationType = 'none' | 'fadeIn' | 'slideUp' | 'slideRight' | 'slideLeft' | 'zoomIn' | 'bounceIn' | 'flipIn';
export type HoverEffect = 'none' | 'lift' | 'glow' | 'scale';
export type AnimationTrigger = 'onLoad' | 'onScroll';

export const ANIMATION_LABELS: Record<AnimationType, string> = {
  none: 'ללא',
  fadeIn: 'הופעה הדרגתית',
  slideUp: 'עלייה מלמטה',
  slideRight: 'כניסה מימין',
  slideLeft: 'כניסה משמאל',
  zoomIn: 'זום פנימה',
  bounceIn: 'קפיצה',
  flipIn: 'סיבוב 3D',
};

export const HOVER_LABELS: Record<HoverEffect, string> = {
  none: 'ללא',
  lift: 'הרמה + צל',
  glow: 'זוהר',
  scale: 'הגדלה',
};

export const TRIGGER_LABELS: Record<AnimationTrigger, string> = {
  onLoad: 'בטעינה',
  onScroll: 'בגלילה',
};

// ─── All @keyframes definitions ──────────────────────────────────────────

export const ANIMATION_KEYFRAMES = `
@keyframes anim-fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes anim-slideUp {
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes anim-slideRight {
  from { opacity: 0; transform: translateX(60px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes anim-slideLeft {
  from { opacity: 0; transform: translateX(-60px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes anim-zoomIn {
  from { opacity: 0; transform: scale(0.7); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes anim-bounceIn {
  0% { opacity: 0; transform: scale(0.3); }
  50% { opacity: 1; transform: scale(1.08); }
  70% { transform: scale(0.95); }
  100% { opacity: 1; transform: scale(1); }
}

@keyframes anim-flipIn {
  from { opacity: 0; transform: perspective(400px) rotateY(90deg); }
  40% { transform: perspective(400px) rotateY(-15deg); }
  70% { transform: perspective(400px) rotateY(10deg); }
  to { opacity: 1; transform: perspective(400px) rotateY(0); }
}

@keyframes anim-progressFill {
  from { width: 0%; }
}

@keyframes anim-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

@keyframes anim-staggerIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
`;

// ─── Get animation CSS for a block ───────────────────────────────────────

export function getAnimationStyle(
  animationType?: AnimationType,
  duration?: number,
  delay?: number,
): React.CSSProperties {
  if (!animationType || animationType === 'none') return {};

  const dur = duration || 600;
  const del = delay || 0;

  return {
    animation: `anim-${animationType} ${dur}ms ease-out ${del}ms both`,
  };
}

// ─── Initial (hidden) state before animation plays ───────────────────────

export function getPreAnimationStyle(animationType?: AnimationType): React.CSSProperties {
  if (!animationType || animationType === 'none') return {};
  return { opacity: 0 };
}

// ─── Hover effect styles ─────────────────────────────────────────────────

export function getHoverEnterStyle(effect?: HoverEffect, accentColor?: string): React.CSSProperties {
  switch (effect) {
    case 'lift':
      return { transform: 'translateY(-6px)', boxShadow: '0 12px 32px rgba(0,0,0,0.15)', transition: 'all 0.3s ease' };
    case 'glow':
      return { boxShadow: `0 0 24px ${accentColor || '#3b82f6'}50`, transition: 'all 0.3s ease' };
    case 'scale':
      return { transform: 'scale(1.04)', transition: 'all 0.3s ease' };
    default:
      return {};
  }
}

export function getHoverLeaveStyle(effect?: HoverEffect): React.CSSProperties {
  switch (effect) {
    case 'lift':
      return { transform: 'translateY(0)', boxShadow: 'none', transition: 'all 0.3s ease' };
    case 'glow':
      return { boxShadow: 'none', transition: 'all 0.3s ease' };
    case 'scale':
      return { transform: 'scale(1)', transition: 'all 0.3s ease' };
    default:
      return {};
  }
}

// ─── Stagger delay for list items (donors, tiers) ────────────────────────

export function getStaggerDelay(index: number, baseDelay = 100): number {
  return index * baseDelay;
}
